
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ExternalLink, Eye, Trash2, Calendar, Clock, Mail, Archive, ArchiveRestore } from 'lucide-react';
import { InterviewRequestResponse } from '@/services/interview/requests/types';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { format, isValid, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface InterviewRequestItemProps {
  request: InterviewRequestResponse;
  onView: (request: InterviewRequestResponse) => void;
  onDelete: (id: string) => void;
  onArchive?: (id: string) => void;
  showUnarchive?: boolean;
}

const InterviewRequestItem: React.FC<InterviewRequestItemProps> = ({
  request,
  onView,
  onDelete,
  onArchive,
  showUnarchive = false
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = parseISO(dateString);
    return isValid(date) ? format(date, 'dd.MM.yyyy HH:mm') : t('interview.invalidDate');
  };

  const getStatusBadge = (status: string, expiresAt: string | null) => {
    if (status === 'completed') {
      return <Badge className="bg-green-500 text-white">{t('interview.completed')}</Badge>;
    }
    
    if (status === 'expired' || (expiresAt && new Date(expiresAt) < new Date())) {
      return <Badge className="bg-red-500 text-white">{t('interview.expired')}</Badge>;
    }
    
    return <Badge className="bg-amber-500 text-white">{t('interview.pending')}</Badge>;
  };

  const handleViewSubmission = () => {
    console.log('Navigating to submission page with request_link:', request.request_link);
    navigate(`/interview-submission/${request.request_link}`);
  };

  const copyLink = () => {
    const submissionUrl = `${window.location.origin}/interview-submission/${request.request_link}`;
    navigator.clipboard.writeText(submissionUrl);
    toast.success(t('interview.linkCopied'));
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    // Soft delete - arkiverer forespørselen i stedet for å slette permanent
    if (onArchive) {
      onArchive(request.id);
    }
    setShowDeleteDialog(false);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-[#ea384c]">
      <div className="flex justify-between items-start mb-4">
        <h3 className="font-semibold text-lg">{request.interviewee_name}</h3>
        {getStatusBadge(request.status, request.expires_at)}
      </div>
      
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600 mb-4">
        <div className="flex items-center gap-1">
          <Mail className="w-4 h-4" />
          {request.interviewee_email}
        </div>
        <div className="flex items-center gap-1">
          <Calendar className="w-4 h-4" />
          {t('interview.createdAt')}: {formatDate(request.created_at)}
        </div>
        {request.expires_at && (
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {t('interview.expiresAt')}: {formatDate(request.expires_at)}
          </div>
        )}
      </div>
      
      <div className="bg-gray-50 p-3 rounded border border-gray-200 mb-3">
        <p className="font-medium">{request.question}</p>
        {request.context && <p className="text-sm text-gray-600 mt-1">{request.context}</p>}
      </div>
      
      <div className="flex flex-wrap gap-2">
        <Button 
          variant="outline" 
          size="sm"
          className="text-xs"
          onClick={copyLink}
        >
          <ExternalLink className="w-3 h-3 mr-1" />
          {t('interview.copyLink')}
        </Button>
        
        <Button 
          variant="outline" 
          size="sm"
          className="text-xs"
          onClick={handleViewSubmission}
        >
          <ExternalLink className="w-3 h-3 mr-1" />
          {t('interview.goToSubmission')}
        </Button>
        
        <Button 
          variant="outline" 
          size="sm"
          className="text-xs"
          onClick={() => onView(request)}
        >
          <Eye className="w-3 h-3 mr-1" />
          {t('interview.viewDetails')}
        </Button>
        
        {onArchive && (
          <Button 
            variant="outline" 
            size="sm"
            className="text-xs text-amber-600 hover:bg-amber-50"
            onClick={() => onArchive(request.id)}
          >
            {showUnarchive ? (
              <>
                <ArchiveRestore className="w-3 h-3 mr-1" />
                Gjenopprett
              </>
            ) : (
              <>
                <Archive className="w-3 h-3 mr-1" />
                Arkiver
              </>
            )}
          </Button>
        )}
        
        <Button 
          variant="outline" 
          size="sm"
          className="text-xs text-red-600 hover:bg-red-50"
          onClick={handleDeleteClick}
        >
          <Trash2 className="w-3 h-3 mr-1" />
          {t('common.delete')}
        </Button>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bekreft arkivering</AlertDialogTitle>
            <AlertDialogDescription>
              Er du sikker på at du vil arkivere denne forespørselen? Den vil bli flyttet til arkivet og kan gjenopprettes senere.
              Mottakeren kan fortsatt svare på forespørselen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>
              Arkiver
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InterviewRequestItem;
